
import { useMemo, useState, useEffect } from "react";
import { DiaryEntry } from "@/utils/diary";
import { format } from "date-fns";

// Reduced from 5 to 3 items per page for faster loading
export const ITEMS_PER_PAGE = 3;

export function useEntriesPagination(
  entries: DiaryEntry[],
  searchTerm: string,
  selectedDate: Date | undefined
) {
  // Get initial page from URL for SEO and shared links
  const getInitialPage = () => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const pageParam = urlParams.get('page');
      return pageParam ? parseInt(pageParam, 10) : 1;
    }
    return 1;
  };

  const [currentPage, setCurrentPage] = useState(getInitialPage);
  
  // Reset to page 1 when entries, search term, or selected date changes
  useEffect(() => {
    setCurrentPage(1);
  }, [entries.length, searchTerm, selectedDate]);
  
  // Filter entries based on search term and selected date
  const filteredEntries = useMemo(() => {
    if (!entries.length) return [];
    
    const lowercaseSearchTerm = searchTerm.toLowerCase();
    const selectedDateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;
    
    return entries.filter(entry => {
      // Search term filtering
      const searchContent = `${entry.babyName || ''} ${entry.entryText || ''} ${entry.mood || ''}`.toLowerCase();
      const matchesSearch = !lowercaseSearchTerm || searchContent.includes(lowercaseSearchTerm);
      
      // Date filtering
      if (selectedDate && matchesSearch) {
        const entryDate = entry.date ? new Date(entry.date) : new Date(entry.createdAt);
        return format(entryDate, 'yyyy-MM-dd') === selectedDateStr;
      }
      
      return matchesSearch;
    });
  }, [entries, searchTerm, selectedDate]);
  
  // Calculate pagination values
  const totalPages = Math.ceil(filteredEntries.length / ITEMS_PER_PAGE);
  
  // Get current page entries - with fewer items per page for faster loading
  const paginatedEntries = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredEntries.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredEntries, currentPage]);
  
  // Page change handler with URL update for SEO
  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
      
      // Update URL for SEO without page reload
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        url.searchParams.set('page', page.toString());
        window.history.pushState({ page }, '', url.toString());
      }
      
      // Scroll to the top of the entries list
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  return {
    filteredEntries,
    paginatedEntries,
    currentPage,
    totalPages,
    handlePageChange
  };
}
